import React from 'react';
import { createPortal } from 'react-dom';
import { X, Search } from 'lucide-react';

/* ==========================================================================
   BUTTONS
   ========================================================================== */

export function PrimaryButton({ children, onClick, type = 'button', disabled = false, loading = false, fullWidth = false, size = 'md', className = '' }) {
  const sizeClasses = {
    sm: 'min-h-10 px-3 py-1.5 text-xs rounded-xl',
    md: 'min-h-11 px-5 py-2.5 text-sm rounded-xl',
    lg: 'min-h-12 px-6 py-3 text-base rounded-2xl'
  }[size];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`font-bold transition-all duration-200 flex items-center justify-center gap-2
        bg-[image:var(--gradient-electric)] text-white border border-[var(--border-electric)] shadow-[var(--glow-blue)] hover:brightness-110 active:scale-[0.98]
        disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
        ${fullWidth ? 'w-full' : ''} ${sizeClasses} ${className}`}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      ) : null}
      {children}
    </button>
  );
}

export function GoldButton({ children, onClick, type = 'button', disabled = false, loading = false, fullWidth = false, size = 'md', className = '' }) {
  const sizeClasses = {
    sm: 'min-h-10 px-3 py-1.5 text-xs rounded-xl',
    md: 'min-h-11 px-5 py-2.5 text-sm rounded-xl',
    lg: 'min-h-12 px-6 py-3 text-base rounded-2xl'
  }[size];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`font-black transition-all duration-200 flex items-center justify-center gap-2
        bg-[image:var(--gradient-gold)] text-slate-950 border border-[var(--brand-gold-highlight)]/50 shadow-[var(--glow-gold)] hover:brightness-105 active:scale-[0.98]
        disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
        ${fullWidth ? 'w-full' : ''} ${sizeClasses} ${className}`}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin" />
      ) : null}
      {children}
    </button>
  );
}

export function SecondaryButton({ children, onClick, type = 'button', disabled = false, fullWidth = false, size = 'md', className = '' }) {
  const sizeClasses = {
    sm: 'min-h-10 px-3 py-1.5 text-xs rounded-xl',
    md: 'min-h-11 px-4 py-2.5 text-sm rounded-xl',
    lg: 'min-h-12 px-6 py-3 text-base rounded-2xl'
  }[size];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled }
      className={`font-bold transition-all duration-200 border border-[var(--color-border)]
        bg-[var(--color-surface-secondary)] text-[var(--color-text-primary)] hover:opacity-80 active:scale-[0.98]
        disabled:opacity-50 disabled:cursor-not-allowed
        ${fullWidth ? 'w-full' : ''} ${sizeClasses} ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick, type = 'button', disabled = false, size = 'md', className = '' }) {
  const sizeClasses = {
    sm: 'px-2.5 py-1 text-xs rounded-lg',
    md: 'px-3 py-2 text-sm rounded-xl',
    lg: 'px-4 py-2.5 text-base rounded-xl'
  }[size];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`font-bold transition-colors text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-secondary)]
        disabled:opacity-50 disabled:cursor-not-allowed ${sizeClasses} ${className}`}
    >
      {children}
    </button>
  );
}

export function DangerButton({ children, onClick, type = 'button', disabled = false, loading = false, size = 'md', className = '' }) {
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs rounded-xl',
    md: 'px-4 py-2 text-sm rounded-xl',
    lg: 'px-5 py-2.5 text-base rounded-2xl'
  }[size];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`font-bold transition-all duration-200 border border-red-500/30
        bg-red-500/10 text-red-500 hover:bg-red-500/20 active:scale-[0.98]
        disabled:opacity-50 disabled:cursor-not-allowed ${sizeClasses} ${className}`}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin inline-block me-2" />
      ) : null}
      {children}
    </button>
  );
}

export function IconButton({ icon: Icon, onClick, title, size = 'md', variant = 'default', className = '' }) {
  const sizeClasses = {
    sm: 'w-8 h-8 p-1.5 rounded-lg',
    md: 'w-10 h-10 p-2.5 rounded-xl',
    lg: 'w-12 h-12 p-3 rounded-2xl'
  }[size];

  const variantClasses = {
    default: 'bg-[var(--color-surface-secondary)] text-[var(--color-text-primary)] hover:opacity-80',
    gold: 'bg-[var(--color-accent)] text-slate-950 hover:brightness-110',
    navy: 'bg-[var(--color-navigation)] text-white hover:opacity-90',
    ghost: 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-secondary)]'
  }[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex items-center justify-center transition-all active:scale-95 shrink-0 ${sizeClasses} ${variantClasses} ${className}`}
    >
      <Icon className="w-full h-full object-contain" />
    </button>
  );
}

/* ==========================================================================
   CARDS
   ========================================================================== */

export function BaseCard({ children, className = '', onClick = null, hover = false, ...props }) {
  return (
    <div
      {...props} onClick={onClick}
      className={`mb-premium-surface min-w-0 border text-[var(--color-text-primary)] rounded-3xl p-4 sm:p-6 shadow-[var(--card-shadow)] transition-all duration-200
        ${hover ? 'hover:shadow-md hover:border-[var(--color-accent)] cursor-pointer' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

export function PremiumCard({ children, className = '', goldBorder = true, bankCardSkin = false }) {
  return (
    <div
      className={`relative overflow-hidden text-white rounded-3xl p-6 shadow-lg border
        ${goldBorder ? 'border-[var(--color-accent)]/40 shadow-[var(--color-accent)]/5' : 'border-slate-800'} ${className}`}
      style={{
        background: bankCardSkin
          ? 'var(--store-card-background, var(--gradient-brand))'
          : 'var(--gradient-brand)',
        color: bankCardSkin ? 'var(--store-card-text, #ffffff)' : '#ffffff',
        borderColor: goldBorder
          ? (bankCardSkin ? 'var(--store-card-accent, var(--color-accent))' : 'var(--color-accent)')
          : undefined,
      }}
    >
      {/* Metallic Subtle Sheen Accent */}
      <div className="absolute top-0 end-0 w-48 h-48 bg-gradient-to-br from-amber-400/10 to-transparent rounded-full blur-2xl pointer-events-none" />
      {children}
    </div>
  );
}

export function StatCard({ label, value, subtext, icon: Icon, color = 'navy', trend = null, className = '' }) {
  return (
    <BaseCard className={`flex flex-col justify-between ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">{label}</span>
        {Icon ? (
          <div className={`p-2 rounded-xl text-xs ${
            color === 'gold' ? 'bg-[var(--color-accent)]/20 text-[var(--color-accent)]' :
            color === 'green' ? 'bg-emerald-500/20 text-emerald-500' :
            'bg-[var(--color-surface-secondary)] text-[var(--color-text-primary)]'
          }`}>
            <Icon className="w-4 h-4" />
          </div>
        ) : null}
      </div>
      <div>
        <div className="text-2xl sm:text-3xl font-black font-num text-[var(--color-text-primary)] tracking-tight">{value}</div>
        {subtext ? <div className="text-xs text-[var(--color-text-secondary)] mt-1 font-medium">{subtext}</div> : null}
      </div>
    </BaseCard>
  );
}

/* ==========================================================================
   INPUTS
   ========================================================================== */

export function Input({ label, error, helperText, className = '', ...props }) {
  return (
    <div className="space-y-1.5 w-full">
      {label ? <label className="block text-xs font-bold text-[var(--color-text-primary)]">{label}</label> : null}
      <input
        className={`w-full px-4 py-2.5 bg-[var(--color-input)] border border-[var(--color-input-border)] rounded-xl text-sm font-medium text-[var(--color-text-primary)]
          placeholder:text-[var(--color-text-secondary)] focus:outline-none focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] transition
          ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''} ${className}`}
        {...props}
      />
      {error ? <p className="text-xs text-red-500 font-medium">{error}</p> : null}
      {helperText && !error ? <p className="text-xs text-[var(--color-text-secondary)]">{helperText}</p> : null}
    </div>
  );
}

export function AmountInput({ label, currency = 'USD', error, onBlur, onChange, ...props }) {
  const step = currency === 'LBPk' ? 5 : currency === 'LBP' ? 5000 : 0.01;
  const normalizeLbp = event => {
    if (currency === 'LBPk' && event.target.value !== '') event.target.value = String(Math.round(Number(event.target.value) / 5) * 5);
    if (currency === 'LBP' && event.target.value !== '') event.target.value = String(Math.round(Number(event.target.value) / 5000) * 5000);
    onChange?.(event);
    onBlur?.(event);
  };
  return (
    <div className="space-y-1.5 w-full">
      {label ? <label className="block text-xs font-bold text-[var(--color-text-primary)]">{label}</label> : null}
      <div className="relative flex items-center">
        <input
          type="number"
          step={step}
          onBlur={normalizeLbp}
          onChange={onChange}
          className={`w-full pe-16 ps-4 py-3 bg-[var(--color-input)] border border-[var(--color-input-border)] rounded-2xl text-lg font-black font-num text-[var(--color-text-primary)]
            placeholder:text-[var(--color-text-secondary)] focus:outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/10 transition
            ${error ? 'border-red-400' : ''}`}
          {...props}
        />
        <div className="absolute end-3 text-xs font-black px-2.5 py-1 rounded-lg bg-[var(--color-surface-secondary)] text-[var(--color-text-primary)] select-none">
          {currency}
        </div>
      </div>
      {error ? <p className="text-xs text-red-500 font-medium">{error}</p> : null}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = 'بحث...', className = '' }) {
  return (
    <div className={`relative flex items-center w-full ${className}`}>
      <Search className="w-4 h-4 absolute start-3 text-[var(--color-text-secondary)] pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full ps-9 pe-4 py-2 bg-[var(--color-input)] border border-[var(--color-input-border)] rounded-xl text-xs font-medium text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:outline-none focus:border-[var(--color-accent)] transition"
      />
    </div>
  );
}

/* ==========================================================================
   BADGES & MODAL
   ========================================================================== */

export function Badge({ children, variant = 'default', className = '' }) {
  const variantClasses = {
    default: 'bg-[var(--color-surface-secondary)] text-[var(--color-text-primary)] border border-[var(--color-border)]',
    gold: 'bg-[var(--color-accent)]/20 text-[var(--color-accent)] border border-[var(--color-accent)]/30 font-bold',
    success: 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30 font-bold',
    danger: 'bg-red-500/20 text-red-500 border border-red-500/30 font-bold',
    warning: 'bg-amber-500/20 text-amber-500 border border-amber-500/30 font-bold',
    info: 'bg-blue-500/20 text-blue-500 border border-blue-500/30 font-bold'
  }[variant];

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-[10px] font-bold ${variantClasses} ${className}`}>
      {children}
    </span>
  );
}

export function ProgressBar({ value = 0, max = 100, color = 'gold', className = '' }) {
  const pct = Math.min(100, Math.max(0, (value / (max || 1)) * 100));
  const colorClass = {
    gold: 'bg-[var(--color-accent)]',
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500'
  }[color] || 'bg-[var(--color-accent)]';

  return (
    <div className={`w-full bg-[var(--color-surface-secondary)] rounded-full overflow-hidden border border-[var(--color-border)] h-2.5 ${className}`}>
      <div
        className={`h-full transition-all duration-300 ${colorClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function Modal({ isOpen, onClose, title, children, maxWidth = 'max-w-md', portal = false }) {
  if (!isOpen) return null;

  const content = (
    <div className="mb-modal-overlay fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={onClose} role="presentation">
      <div
        className={`min-w-0 bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-primary)] rounded-2xl sm:rounded-3xl w-full ${maxWidth} max-h-[calc(100dvh-1.5rem)] overflow-y-auto p-4 sm:p-6 relative shadow-2xl fade-up`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] pb-3 mb-4">
          <h3 className="min-w-0 break-words text-base font-black text-[var(--color-text-primary)]">{title}</h3>
          <button type="button" aria-label="إغلاق" onClick={onClose} className="shrink-0 p-1 rounded-lg hover:bg-[var(--color-surface-secondary)] text-[var(--color-text-secondary)]">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
  return portal ? createPortal(content, document.body) : content;
}
